import { Dealer } from "@/types/dealer";
import { isSSR } from "@/lib/isSSR";
import { 
  RECOMMENDED_DEALERS_CACHE_CONFIG, 
  generateCacheKey, 
  isStale, 
  isExpired 
} from "./cacheConfig";

// Interfaccia per i dati cachati
interface CachedData {
  data: Dealer[];
  timestamp: number;
  version: string;
  metal: string;
  coordinates?: { lat: number; lng: number };
}

// Interfaccia per le statistiche della cache
interface CacheStats {
  hits: number;
  misses: number;
  sets: number;
  invalidations: number;
}

// Dichiarazione globale per la cache
declare global {
  interface Window {
    __ENHANCED_DEALERS_CACHE__?: {
      [key: string]: CachedData;
    };
    __DEALERS_CACHE_STATS__?: CacheStats;
  }
}

/**
 * Sistema di cache multi-livello per compro oro consigliati
 * Livello 1: Memoria (più veloce)
 * Livello 2: localStorage (persistente)
 * Livello 3: Server-side cache (per SSR)
 */
export class EnhancedRecommendedDealersCache {
  private static instance: EnhancedRecommendedDealersCache;
  private memoryCache: Map<string, CachedData> = new Map();
  private stats: CacheStats = { hits: 0, misses: 0, sets: 0, invalidations: 0 };

  private constructor() {
    this.initializeCache();
  }

  public static getInstance(): EnhancedRecommendedDealersCache {
    if (!EnhancedRecommendedDealersCache.instance) {
      EnhancedRecommendedDealersCache.instance = new EnhancedRecommendedDealersCache();
    }
    return EnhancedRecommendedDealersCache.instance;
  }

  /**
   * Inizializza la cache e migra i dati esistenti
   */
  private initializeCache(): void {
    if (isSSR()) return;

    // Inizializza cache globale
    if (typeof window !== 'undefined') {
      window.__ENHANCED_DEALERS_CACHE__ = window.__ENHANCED_DEALERS_CACHE__ || {};
      window.__DEALERS_CACHE_STATS__ = window.__DEALERS_CACHE_STATS__ || this.stats;
      this.stats = window.__DEALERS_CACHE_STATS__;
    }

    // Migra dati dalla vecchia cache se esistenti
    this.migrateOldCache();
    
    // Pulisce cache scadute
    this.cleanupExpiredCache();

    console.log("🚀 Enhanced Dealers Cache initialized");
  }

  /**
   * Migra i dati dalla vecchia implementazione della cache
   */
  private migrateOldCache(): void {
    if (typeof window === 'undefined') return;

    try {
      // Migra da window.__DEALER_CACHE__
      const oldCache = window.__DEALER_CACHE__;
      if (oldCache) {
        Object.entries(oldCache).forEach(([key, value]) => {
          if (value && value.data && Array.isArray(value.data)) {
            const metal = key.includes('ORO') ? 'ORO' : 'ARGENTO';
            const newKey = generateCacheKey(metal);
            
            const cachedData: CachedData = {
              data: value.data,
              timestamp: value.timestamp || Date.now(),
              version: RECOMMENDED_DEALERS_CACHE_CONFIG.CACHE_VERSION,
              metal
            };
            
            this.memoryCache.set(newKey, cachedData);
            this.saveToLocalStorage(newKey, cachedData);
          }
        });
        
        // Pulisce la vecchia cache
        window.__DEALER_CACHE__ = {};
        console.log("✅ Migrated old cache data");
      }

      // Migra da localStorage con vecchie chiavi
      const oldKeys = ['recommended-dealers-data-ORO', 'recommended-dealers-data-ARGENTO'];
      oldKeys.forEach(oldKey => {
        const oldData = localStorage.getItem(oldKey);
        if (oldData) {
          try {
            const parsed = JSON.parse(oldData);
            if (Array.isArray(parsed)) {
              const metal = oldKey.includes('ORO') ? 'ORO' : 'ARGENTO';
              const newKey = generateCacheKey(metal);
              
              const cachedData: CachedData = {
                data: parsed,
                timestamp: Date.now(),
                version: RECOMMENDED_DEALERS_CACHE_CONFIG.CACHE_VERSION,
                metal
              };
              
              this.memoryCache.set(newKey, cachedData);
              this.saveToLocalStorage(newKey, cachedData);
            }
          } catch (error) {
            console.warn(`Failed to migrate old cache key ${oldKey}:`, error);
          }
          
          // Rimuove la vecchia chiave
          localStorage.removeItem(oldKey);
        }
      });
    } catch (error) {
      console.warn("Failed to migrate old cache:", error);
    }
  }

  /**
   * Pulisce le cache scadute
   */
  private cleanupExpiredCache(): void {
    if (typeof window === 'undefined') return;

    try {
      // Pulisce memoria cache
      for (const [key, data] of this.memoryCache.entries()) {
        if (isExpired(data.timestamp)) {
          this.memoryCache.delete(key);
        }
      }

      // Pulisce localStorage
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(RECOMMENDED_DEALERS_CACHE_CONFIG.CACHE_KEYS.LOCALSTORAGE)) {
          try {
            const data = localStorage.getItem(key);
            if (data) {
              const parsed: CachedData = JSON.parse(data);
              if (isExpired(parsed.timestamp)) {
                keysToRemove.push(key);
              }
            }
          } catch (error) {
            keysToRemove.push(key);
          }
        }
      }

      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      if (keysToRemove.length > 0) {
        console.log(`🧹 Cleaned up ${keysToRemove.length} expired cache entries`);
      }
    } catch (error) {
      console.warn("Failed to cleanup expired cache:", error);
    }
  }

  /**
   * Ottiene dati dalla cache (multi-livello)
   */
  public get(metal: string, coordinates?: { lat: number; lng: number }): Dealer[] | null {
    const key = generateCacheKey(metal, coordinates);

    // Livello 1: Memoria cache
    const memoryData = this.getFromMemory(key);
    if (memoryData && !isExpired(memoryData.timestamp)) {
      this.stats.hits++;
      console.log(`💾 Cache HIT (memory) for ${metal}`, { 
        age: Date.now() - memoryData.timestamp,
        count: memoryData.data.length 
      });
      return memoryData.data;
    }

    // Livello 2: localStorage
    const localData = this.getFromLocalStorage(key);
    if (localData && !isExpired(localData.timestamp)) {
      // Ripopola la memoria cache
      this.memoryCache.set(key, localData);
      this.stats.hits++;
      console.log(`💿 Cache HIT (localStorage) for ${metal}`, { 
        age: Date.now() - localData.timestamp,
        count: localData.data.length 
      });
      return localData.data;
    }

    this.stats.misses++;
    console.log(`❌ Cache MISS for ${metal}`);
    return null;
  }

  /**
   * Salva dati nella cache (multi-livello)
   */
  public set(
    metal: string, 
    data: Dealer[], 
    coordinates?: { lat: number; lng: number }
  ): void {
    const key = generateCacheKey(metal, coordinates);
    const cachedData: CachedData = {
      data,
      timestamp: Date.now(),
      version: RECOMMENDED_DEALERS_CACHE_CONFIG.CACHE_VERSION,
      metal,
      coordinates
    };

    // Salva in memoria
    this.memoryCache.set(key, cachedData);

    // Salva in localStorage
    this.saveToLocalStorage(key, cachedData);

    // Aggiorna cache globale
    if (typeof window !== 'undefined' && window.__ENHANCED_DEALERS_CACHE__) {
      window.__ENHANCED_DEALERS_CACHE__[key] = cachedData;
    }

    this.stats.sets++;
    console.log(`💾 Cached ${data.length} dealers for ${metal}`, {
      key,
      hasCoordinates: !!coordinates
    });
  }

  /**
   * Verifica se i dati sono stale (ma non scaduti)
   */
  public isStale(metal: string, coordinates?: { lat: number; lng: number }): boolean {
    const key = generateCacheKey(metal, coordinates);
    const data = this.getFromMemory(key) || this.getFromLocalStorage(key);
    
    if (!data) return true;
    return isStale(data.timestamp);
  }

  /**
   * Invalida cache per un metallo specifico
   */
  public invalidate(metal?: string): void {
    if (metal) {
      // Invalida solo per il metallo specifico
      const keysToRemove = Array.from(this.memoryCache.keys())
        .filter(key => key.includes(metal));
      
      keysToRemove.forEach(key => {
        this.memoryCache.delete(key);
        this.removeFromLocalStorage(key);
      });
      
      console.log(`🗑️ Invalidated cache for ${metal}`);
    } else {
      // Invalida tutta la cache
      this.memoryCache.clear();
      this.clearLocalStorage();
      
      if (typeof window !== 'undefined') {
        window.__ENHANCED_DEALERS_CACHE__ = {};
      }
      
      console.log("🗑️ Invalidated all cache");
    }
    
    this.stats.invalidations++;
  }

  /**
   * Pre-carica dati per entrambi i metalli
   */
  public async warmCache(
    fetchFunction: (metal: string) => Promise<Dealer[]>,
    coordinates?: { lat: number; lng: number }
  ): Promise<void> {
    if (!RECOMMENDED_DEALERS_CACHE_CONFIG.CACHE_WARMING.ENABLED) return;

    const metals = RECOMMENDED_DEALERS_CACHE_CONFIG.CACHE_WARMING.METALS;
    
    console.log("🔥 Starting cache warming for metals:", metals);
    
    const promises = metals.map(async (metal) => {
      try {
        // Controlla se abbiamo già dati freschi
        const existing = this.get(metal, coordinates);
        if (existing && !this.isStale(metal, coordinates)) {
          console.log(`⏭️ Skipping cache warming for ${metal} (fresh data exists)`);
          return;
        }

        const data = await fetchFunction(metal);
        this.set(metal, data, coordinates);
        console.log(`🔥 Cache warmed for ${metal}: ${data.length} dealers`);
      } catch (error) {
        console.warn(`Failed to warm cache for ${metal}:`, error);
      }
    });

    await Promise.allSettled(promises);
    console.log("🔥 Cache warming completed");
  }

  /**
   * Ottiene statistiche della cache
   */
  public getStats(): CacheStats & { memorySize: number; localStorageSize: number } {
    return {
      ...this.stats,
      memorySize: this.memoryCache.size,
      localStorageSize: this.getLocalStorageSize()
    };
  }

  // Metodi privati per gestione storage

  private getFromMemory(key: string): CachedData | null {
    return this.memoryCache.get(key) || null;
  }

  private getFromLocalStorage(key: string): CachedData | null {
    if (typeof localStorage === 'undefined') return null;

    try {
      const stored = localStorage.getItem(`${RECOMMENDED_DEALERS_CACHE_CONFIG.CACHE_KEYS.LOCALSTORAGE}-${key}`);
      if (stored) {
        const parsed: CachedData = JSON.parse(stored);
        // Verifica versione
        if (parsed.version === RECOMMENDED_DEALERS_CACHE_CONFIG.CACHE_VERSION) {
          return parsed;
        }
      }
    } catch (error) {
      console.warn("Failed to get from localStorage:", error);
    }

    return null;
  }

  private saveToLocalStorage(key: string, data: CachedData): void {
    if (typeof localStorage === 'undefined') return;

    try {
      const storageKey = `${RECOMMENDED_DEALERS_CACHE_CONFIG.CACHE_KEYS.LOCALSTORAGE}-${key}`;
      localStorage.setItem(storageKey, JSON.stringify(data));
    } catch (error) {
      console.warn("Failed to save to localStorage:", error);
      // Se localStorage è pieno, prova a pulire vecchi dati
      this.cleanupExpiredCache();
    }
  }

  private removeFromLocalStorage(key: string): void {
    if (typeof localStorage === 'undefined') return;

    try {
      const storageKey = `${RECOMMENDED_DEALERS_CACHE_CONFIG.CACHE_KEYS.LOCALSTORAGE}-${key}`;
      localStorage.removeItem(storageKey);
    } catch (error) {
      console.warn("Failed to remove from localStorage:", error);
    }
  }

  private clearLocalStorage(): void {
    if (typeof localStorage === 'undefined') return;

    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(RECOMMENDED_DEALERS_CACHE_CONFIG.CACHE_KEYS.LOCALSTORAGE)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
    } catch (error) {
      console.warn("Failed to clear localStorage:", error);
    }
  }

  private getLocalStorageSize(): number {
    if (typeof localStorage === 'undefined') return 0;

    let count = 0;
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(RECOMMENDED_DEALERS_CACHE_CONFIG.CACHE_KEYS.LOCALSTORAGE)) {
          count++;
        }
      }
    } catch (error) {
      console.warn("Failed to get localStorage size:", error);
    }

    return count;
  }
}

// Esporta istanza singleton
export const enhancedDealersCache = EnhancedRecommendedDealersCache.getInstance();

// Utility functions per compatibilità con il sistema esistente
export const getCachedRecommendedDealers = (
  metal: string, 
  coordinates?: { lat: number; lng: number }
): Dealer[] | null => {
  return enhancedDealersCache.get(metal, coordinates);
};

export const cacheRecommendedDealers = (
  metal: string, 
  data: Dealer[], 
  coordinates?: { lat: number; lng: number }
): void => {
  enhancedDealersCache.set(metal, data, coordinates);
};

export const invalidateRecommendedDealersCache = (metal?: string): void => {
  enhancedDealersCache.invalidate(metal);
};

export const isRecommendedDealersStale = (
  metal: string, 
  coordinates?: { lat: number; lng: number }
): boolean => {
  return enhancedDealersCache.isStale(metal, coordinates);
};
